import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Input, Form } from 'antd';
import { PlusOutlined, SearchOutlined, UserOutlined, ReloadOutlined } from '@ant-design/icons';
import { renderDateTime, ActionButtons } from './utils';
import type { ColumnsType } from 'antd/es/table';
import { ModalForm, ProFormText } from '@ant-design/pro-components';

interface Purchaser {
  id: number;
  credit_code: string;
  account: string;
  company_name: string;
  contact_name: string;
  contact_phone: string;
  company_address: string;
  create_time: string;
}

export default function AccountPurchaser() {
  const [purchasers, setPurchasers] = useState<Purchaser[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Purchaser | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPurchasers();
  }, []);

  const fetchPurchasers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/purchasers', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setPurchasers(resp.data || []);
      } else {
        message.error(resp?.error || '获取采购人列表失败');
      }
    } catch (err) {
      message.error('获取采购人列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Purchaser) => {
    setEditingItem(record);
    form.setFieldsValue({
      credit_code: record.credit_code,
      account: record.account,
      company_name: record.company_name,
      contact_name: record.contact_name,
      contact_phone: record.contact_phone,
      company_address: record.company_address,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/purchasers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchPurchasers();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingItem 
        ? `/api/admin/purchasers/${editingItem.id}`
        : '/api/admin/purchasers';
      const method = editingItem ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingItem ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchPurchasers();
        return true;
      } else {
        message.error(resp?.error || '操作失败');
        return false;
      }
    } catch (err) {
      message.error('操作失败');
      return false;
    }
  };

  // 过滤数据
  const filteredData = purchasers.filter(p => 
    p.credit_code?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    p.account?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    p.company_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    p.contact_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    p.contact_phone?.includes(searchKeyword)
  );

  const columns: ColumnsType<Purchaser> = [
    {
      title: '社会信用码',
      dataIndex: 'credit_code',
      key: 'credit_code',
      width: 200,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '账号名称',
      dataIndex: 'account',
      key: 'account',
      width: 150,
    },
    {
      title: '单位名称',
      dataIndex: 'company_name',
      key: 'company_name',
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '经办人名称',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 120,
    },
    {
      title: '经办人联系方式',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      width: 140,
    },
    {
      title: '公司地址',
      dataIndex: 'company_address',
      key: 'company_address',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 170,
      render: renderDateTime,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_, record) => (
        <ActionButtons
          onEdit={() => handleEdit(record)}
          onDelete={() => handleDelete(record.id)}
          deleteTitle="确定要删除这个采购人吗？"
        />
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <UserOutlined />
          </div>
          <div>
            <h2>采购人管理</h2>
            <p>管理所有采购人账号</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索采购人..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchPurchasers}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            新增采购人
          </Button>
        </Space>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UserOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingItem ? '编辑采购人' : '新增采购人'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={600}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
        submitter={{
          searchConfig: { submitText: '确定', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="credit_code"
            label="社会信用码"
            placeholder="请输入社会信用码"
            rules={[{ required: true, message: '请输入社会信用码' }]}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormText
              name="account"
              label="账号名称"
              placeholder="请输入账号名称"
              rules={[{ required: true, message: '请输入账号名称' }]}
            />
            <ProFormText
              name="password"
              label={editingItem ? "新密码" : "密码"}
              placeholder={editingItem ? "如需修改密码请输入" : "请输入密码"}
              fieldProps={{ type: 'password' }}
              rules={editingItem ? [] : [{ required: true, message: '请输入密码' }]}
            />
          </div>
          <ProFormText
            name="company_name"
            label="单位名称"
            placeholder="请输入单位名称"
            rules={[{ required: true, message: '请输入单位名称' }]}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormText
              name="contact_name"
              label="经办人名称"
              placeholder="请输入经办人名称"
            />
            <ProFormText
              name="contact_phone"
              label="经办人联系方式"
              placeholder="请输入联系方式"
            />
          </div>
          <ProFormText
            name="company_address"
            label="公司地址"
            placeholder="请输入公司地址"
          />
        </div>
      </ModalForm>
    </div>
  );
}

